import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import {
  CommonInput,
  CommonSelect,
  CommonDatePicker,
  CommonButton,
  CommonModal,
} from "../Input";
import Swal from "sweetalert2";
import countries from "../../../data/countries";

const IndividualCustomerModal = ({ show, onHide, customer }) => {
  const isEdit = !!customer;

  const options = {
    nationality: countries?.map((country) => ({
      value: country.name,
      label: country.name,
      flag: country.flag,
    })),
    gender: [
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
      { value: "other", label: "Other" },
    ],
    occupation: [
      { value: "engineer", label: "Engineer" },
      { value: "teacher", label: "Teacher" },
      { value: "doctor", label: "Doctor" },
      { value: "student", label: "Student" },
    ],
    country: countries?.map((country) => ({
      value: country.name,
      label: country.name,
      flag: country.flag,
    })),
    idType: [
      { value: "nric", label: "NRIC" },
      { value: "passport", label: "Passport" },
      { value: "fin", label: "FIN" },
    ],
    phonePrefix: countries?.map((country) => ({
      value: country.dial,
      label: `${country.dial} (${country.code})`,
      flag: country.flag,
    })),
  };

  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    dob: "",
    nationality: null,
    countryOfBirth: null,
    gender: null,
    occupation: null,
    phonePrefix: options.phonePrefix.find((p) => p.value === "+65") || null,
    phone: "",
    email: "",
    country: null,
    city: "",
    address1: "",
    address2: "",
    postalCode: "",
    idType: null,
    idNumber: "",
    issueDate: "",
  });

  useEffect(() => {
    if (show) {
      if (customer) {
        // Map customer data (from mock/API) to form fields
        // Note: Mock data has name, email, phone, idCard
        const phonePrefixValue = customer.phone?.split(" ")[0] || "+65";
        const phoneValue = customer.phone?.split(" ").slice(1).join(" ") || "";

        setFormData({
          firstName: customer.name?.split(" ")[0] || "",
          middleName:
            customer.name?.split(" ").slice(1, -1).join(" ") || "manual",
          lastName: customer.name?.split(" ").pop() || "transaction",
          dob: "06/02/2008", // Mock default
          nationality: options.nationality.find(
            (o) => o.value.toLowerCase() === "singaporean",
          ),
          countryOfBirth: options.country.find(
            (o) => o.value.toLowerCase() === "philippines",
          ),
          gender: options.gender.find((o) => o.value === "male"),
          occupation: options.occupation.find((o) => o.value === "engineer"),
          phonePrefix:
            options.phonePrefix.find((p) => p.value === phonePrefixValue) ||
            options.phonePrefix.find((p) => p.value === "+65"),
          phone: phoneValue,
          email: customer.email || "",
          country: options.country.find(
            (o) => o.value.toLowerCase() === "philippines",
          ),
          city: "hochiminh",
          address1: "hochiminh",
          address2: "HOCHIMINH",
          postalCode: "213123",
          idType: options.idType.find((o) => o.value === "nric"),
          idNumber: customer.idCard || "",
          issueDate: "04/02/2026",
        });
      } else {
        setFormData({
          firstName: "",
          middleName: "",
          lastName: "",
          dob: "",
          phonePrefix:
            options.phonePrefix.find((p) => p.value === "+65") || null,
          phone: "",
          email: "",
          // ... rest reset
        });
      }
    }
  }, [show, customer]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const formatOptionLabel = ({ label, flag, value }, { context }) => (
    <div className="d-flex align-items-center">
      {flag && (
        <img
          src={flag}
          alt=""
          style={{ width: "20px", marginRight: "8px", borderRadius: "2px" }}
        />
      )}
      <span style={{ fontSize: "13px" }}>
        {context === "value" ? value : label}
      </span>
    </div>
  );

  const [files, setFiles] = useState({});

  const handleFileChange = (key, event) => {
    const file = event.target.files[0];
    if (file) {
      setFiles((prev) => ({ ...prev, [key]: file }));
    }
  };

  const removeFile = (key, e) => {
    e.stopPropagation();
    setFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[key];
      return newFiles;
    });
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    Swal.fire({
      icon: "success",
      title: "Success",
      text: isEdit
        ? "Customer updated successfully!"
        : "Customer created successfully!",
      confirmButtonColor: "#00264D",
    }).then(() => {
      onHide();
    });
  };

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      size="xl"
      title={isEdit ? "Edit Customer Details" : "Create New Customer"}
      footer={
        <>
          <CommonButton variant="outline" onClick={onHide} className="me-2">
            Close
          </CommonButton>
          <CommonButton variant="primary" onClick={handleSubmit}>
            {isEdit ? "Save Changes" : "Create Customer"}
          </CommonButton>
        </>
      }
    >
      <p className="mb-4 text-muted fs-14">
        {isEdit
          ? "Update the customer information below."
          : "Fill in the customer information to create a new individual customer."}
      </p>
      <form onSubmit={handleSubmit}>
        {/* Customer Type Section */}
        <div className="mb-4">
          <CommonInput
            label="Customer Type"
            value="Individual"
            readOnly
            wrapClass="w-100"
          />
        </div>

        {/* Personal Information */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Personal Information</h6>
        </div>
        <Row className="mb-4">
          <Col md={3} className="mb-3">
            <CommonInput
              label="First Name"
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label={
                <span>
                  Middle Name{" "}
                  <i className="fas fa-info-circle text-muted ms-1 fs-12"></i>
                </span>
              }
              placeholder="Enter middle name"
              value={formData.middleName}
              onChange={(e) => handleChange("middleName", e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonInput
              label="Last Name"
              placeholder="Enter last name"
              value={formData.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonDatePicker
              label="Date of Birth"
              value={formData.dob}
              onChange={(val) => handleChange("dob", val)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonSelect
              label="Nationality"
              placeholder="Select an option"
              options={options.nationality}
              formatOptionLabel={formatOptionLabel}
              value={formData.nationality}
              onChange={(val) => handleChange("nationality", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonSelect
              label="Country of Birth"
              placeholder="Select an option"
              options={options.country}
              formatOptionLabel={formatOptionLabel}
              value={formData.countryOfBirth}
              onChange={(val) => handleChange("countryOfBirth", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonSelect
              label="Gender"
              placeholder="Select an option"
              options={options.gender}
              value={formData.gender}
              onChange={(val) => handleChange("gender", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonSelect
              label="Occupation"
              placeholder="Select an option"
              options={options.occupation}
              value={formData.occupation}
              onChange={(val) => handleChange("occupation", val)}
            />
          </Col>
        </Row>

        {/* Contact Information */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Contact Information</h6>
        </div>
        <Row className="mb-4">
          <Col md={6} className="mb-3">
            <label className="text-black fw-bold mb-1">Phone Number</label>
            <div className="d-flex">
              <div style={{ width: "130px", marginRight: "10px" }}>
                <CommonSelect
                  placeholder="+65"
                  options={options.phonePrefix}
                  value={formData.phonePrefix}
                  onChange={(val) => handleChange("phonePrefix", val)}
                  formatOptionLabel={formatOptionLabel}
                  styles={{
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0 4px",
                    }),
                  }}
                />
              </div>
              <div className="flex-grow-1">
                <CommonInput
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                />
              </div>
            </div>
          </Col>
          <Col md={6} className="mb-3">
            <CommonInput
              label="Email"
              placeholder="Enter email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
          </Col>
        </Row>

        {/* Address Information */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Address Information</h6>
        </div>
        <Row className="mb-4">
          <Col md={2} className="mb-3">
            <CommonSelect
              label="Country"
              placeholder="Select an option"
              options={options.country}
              formatOptionLabel={formatOptionLabel}
              value={formData.country}
              onChange={(val) => handleChange("country", val)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonInput
              label="City"
              placeholder="Enter city"
              value={formData.city}
              onChange={(e) => handleChange("city", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Address Line 1"
              placeholder="Enter address line 1"
              value={formData.address1}
              onChange={(e) => handleChange("address1", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Address Line 2 (Optional)"
              placeholder="Enter address line 2"
              value={formData.address2}
              onChange={(e) => handleChange("address2", e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonInput
              label="Postal Code"
              placeholder="Enter postal code"
              value={formData.postalCode}
              onChange={(e) => handleChange("postalCode", e.target.value)}
            />
          </Col>
        </Row>

        {/* ID Information */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">ID Information</h6>
        </div>
        <Row className="mb-4">
          <Col md={4} className="mb-3">
            <CommonSelect
              label="ID Type"
              placeholder="Select an option"
              options={options.idType}
              value={formData.idType}
              onChange={(val) => handleChange("idType", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="ID Number"
              placeholder="Enter ID number"
              value={formData.idNumber}
              onChange={(e) => handleChange("idNumber", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonDatePicker
              label="Issue Date"
              value={formData.issueDate}
              onChange={(val) => handleChange("issueDate", val)}
            />
          </Col>
        </Row>

        {/* Document Upload */}
        <div
          className="bg-light p-2 mb-3 rounded"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <h6 className="mb-0 fw-bold px-1" style={{ color: "#333" }}>
            Document Upload
          </h6>
        </div>
        <Row className="mb-4">
          {[
            {
              key: "id_front",
              label: "Front of ID Card",
              desc: "Upload front image of your ID card",
              small: "Upload front of ID card",
            },
            {
              key: "id_back",
              label: "Back of ID Card",
              desc: "Upload back image of your ID card",
              small: "Upload back of ID card",
            },
            {
              key: "selfie",
              label: "Selfie (Optional)",
              desc: "Upload a clear photo of yourself",
              small: "Upload selfie",
            },
            {
              key: "address_proof",
              label: "Proof of Address (Optional)",
              desc: "Upload document showing your address",
              small: "Upload proof of address",
            },
          ].map((box, i) => (
            <Col md={3} key={i} className="mb-3 d-flex flex-column">
              <label className="text-black fw-bold mb-2 fs-13 flex-grow-0 ps-1">
                {box.label}
              </label>
              <div
                className="upload-box-premium d-flex p-3 align-items-center gap-3 w-100 flex-grow-1"
                style={{
                  border: files[box.key]
                    ? "1.5px solid #003b95"
                    : "1.5px dashed #cfd9e0",
                  borderRadius: "12px",
                  cursor: "pointer",
                  height: "100px",
                  backgroundColor: files[box.key] ? "#f0f7ff" : "#ffffff",
                  transition: "all 0.25s ease-in-out",
                  position: "relative",
                  overflow: "hidden",
                }}
                onClick={() =>
                  document.getElementById(`file-input-${box.key}`).click()
                }
              >
                <input
                  type="file"
                  id={`file-input-${box.key}`}
                  className="d-none"
                  onChange={(e) => handleFileChange(box.key, e)}
                  accept="image/*,.pdf"
                />
                <div
                  className="icon-container d-flex align-items-center justify-content-center"
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "10px",
                    backgroundColor: files[box.key] ? "#003b95" : "#f4f7fa",
                    color: files[box.key] ? "#ffffff" : "#003b95",
                    fontSize: "20px",
                    flexShrink: 0,
                  }}
                >
                  <i
                    className={`fas ${files[box.key] ? "fa-check" : "fa-cloud-upload-alt"}`}
                  ></i>
                </div>
                <div className="flex-grow-1 overflow-hidden">
                  <p
                    className="mb-1 fw-bold text-dark fs-12 text-truncate"
                    style={{ letterSpacing: "0.2px" }}
                  >
                    {files[box.key] ? files[box.key].name : box.desc}
                  </p>
                  <div className="d-flex align-items-center text-muted">
                    <span className="fs-10 opacity-75">
                      {files[box.key]
                        ? `${(files[box.key].size / 1024).toFixed(1)} KB`
                        : box.small}
                    </span>
                  </div>
                </div>
                {files[box.key] && (
                  <div
                    className="position-absolute"
                    style={{ top: "10px", right: "10px" }}
                    onClick={(e) => removeFile(box.key, e)}
                  >
                    <i className="fas fa-times-circle text-danger fs-14"></i>
                  </div>
                )}
              </div>
            </Col>
          ))}
        </Row>
      </form>
    </CommonModal>
  );
};

export default IndividualCustomerModal;
